import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import get from 'lodash.get'
import { useHideIfNoDataWhen } from '../../HideIfNoData'
import { useFormattingContext } from '../../FormattingProvider/FormattingContext'
import OpalPresentationTable, { usePresentationTableExpandState } from '../../DenaliAnalysis/tables/presentation'
import { getInitialExpandedState } from '../../DenaliAnalysis/tables/presentation/utils'
import { useCollapsibleColumns } from '../../../../hooks'
import { usePipelineDataContextWithVariables } from '../provider/DDPDataProvider'
import { ExportTableToggle } from './ExportTableToggle'
import { extractPivotValuesByGrouping } from './utils/pivotExtraction'
import { generateGroupingPivotColumns } from './utils/pivotColumns'
import { filterByGrouping } from './utils/dataFiltering'

const useColumns = (columns, pivotConfig, data, totals) => {
  const { formatter } = useFormattingContext()

  return useMemo(() => {
    if (!columns) return { columns: [], defaultSort: [], includeFooter: false }

    const defaultSort = []
    const includeFooter = totals?.length > 0

    const processedColumns = [...columns]

    if (pivotConfig) {
      const pivotIndex = processedColumns.findIndex(col => col.header === '*' || col.isPivot)
      if (pivotIndex >= 0) {
        const pivotColumn = processedColumns[pivotIndex]
        const { pivotAccessor, pivotValueAccessor, enableColumnGrouping, getSubRows } = pivotConfig
        const { format = 'marketValue', align = 'right', width = 140 } = pivotColumn
        const pivotValues = extractPivotValuesByGrouping(data || [], pivotAccessor, getSubRows)
        const generatedColumns = generateGroupingPivotColumns(
          pivotValues,
          pivotAccessor,
          pivotValueAccessor,
          format,
          align,
          width,
          getSubRows,
          enableColumnGrouping
        )
        processedColumns.splice(pivotIndex, 1, ...generatedColumns)
      }
    }

    const mapColumnProperties = (column) => {
      const accessorKey = column.accessorKey || column.accessor
      return {
        ...column,
        header: typeof column.header === 'function' ? column.header : () => column.header,
        accessorKey,
        size: column.size || column.width,
        maxSize: column.maxSize || column.maxWidth,
        minSize: column.minSize || column.minWidth,
        meta: {
          ...column.meta,
          highlight: (row) => row?.original?.comparison?.includes(accessorKey) ?? false
        }
      }
    }

    const addFormatting = (column, mappedColumn, accessorKey) => {
      if (!mappedColumn?.cell) {
        mappedColumn.cell = (props) => {
          const value = typeof column.accessor === 'function'
            ? column.accessor(props.row.original)
            : props.getValue()
          return formatter(value, column.format)
        }
      }
      if (includeFooter && !mappedColumn?.footer) {
        const total = totals.at(0)
        mappedColumn.footer = () => {
          const value = typeof column.accessor === 'function' ? column.accessor(total) : total[accessorKey]
          return formatter(value, column.format)
        }
      }
      return mappedColumn
    }

    const templatedColumns = processedColumns.map((column) => {
      const result = mapColumnProperties(column)

      if (result.columns) {
        result.columns = column.columns.map((subColumn) => {
          const subColResult = mapColumnProperties(subColumn)
          return addFormatting(subColumn, subColResult, subColumn.accessorKey || subColumn.accessor)
        })
      }

      return addFormatting(column, result, column.accessorKey || column.accessor)
    })

    return {
      columns: templatedColumns,
      defaultSort,
      includeFooter
    }
  }, [formatter, columns, pivotConfig, data, totals])
}

function PipelinePivotTable ({
  pipeline,
  columnConfig,
  groupingLevels,
  pivotAccessor,
  pivotValueAccessor,
  enableColumnGrouping,
  defaultColumnsExpanded,
  getSubRows,
  headerMode,
  className,
  style,
  componentClasses,
  componentStyles,
  dataAccessor,
  variables,
  emptyStateText,
  initialExpandDepth,
  allowExport,
  groupRowsBy,
  ...tableProps
}) {
  const { data, isLoading } = usePipelineDataContextWithVariables(pipeline, variables)

  const { tableData, unfilteredData } = useMemo(() => {
    const extracted = get(data, dataAccessor)
    if (groupingLevels && Array.isArray(groupingLevels) && groupingLevels.length > 0) {
      const filtered = filterByGrouping(extracted, extracted, groupingLevels, getSubRows, [], groupRowsBy)
      return { tableData: filtered, unfilteredData: extracted }
    }
    return { tableData: extracted, unfilteredData: extracted }
  }, [data, dataAccessor, groupingLevels, getSubRows, groupRowsBy])

  const initialExpansionState = useMemo(() => {
    if (initialExpandDepth !== undefined && !!tableData?.length) {
      return getInitialExpandedState(tableData, initialExpandDepth, getSubRows)
    }
    return true
  }, [tableData, initialExpandDepth, getSubRows])

  const { expandState, setExpandState } = usePresentationTableExpandState(initialExpansionState)

  const pivotConfig = useMemo(() => {
    if (!pivotAccessor) return null
    return { pivotAccessor, pivotValueAccessor, enableColumnGrouping, getSubRows }
  }, [pivotAccessor, pivotValueAccessor, enableColumnGrouping, getSubRows])

  const { columns, defaultSort, includeFooter } = useColumns(
    columnConfig,
    pivotConfig,
    unfilteredData,
    data?.total || []
  )

  const { visibleColumns, collapsedGroups, toggleGroup } = useCollapsibleColumns(
    columns,
    defaultColumnsExpanded,
    enableColumnGrouping
  )

  useHideIfNoDataWhen(() => !isLoading && !tableData?.length, [tableData, isLoading])

  return (
    <div>
      {allowExport && (
        <div className='flex justify-end mb-2'>
          <ExportTableToggle
            pipeline={pipeline}
            tableData={tableData}
            columns={columns}
            isLoading={isLoading}
          />
        </div>
      )}
      <div style={{ overflowX: 'auto' }}>
        <OpalPresentationTable
          className={className}
          componentClasses={componentClasses}
          componentStyles={{
            ...componentStyles,
            highlight: componentStyles?.highlight || { backgroundColor: 'var(--color-highlight-500)' }
          }}
          style={{ tableLayout: 'fixed', ...style }}
          data={tableData || []}
          columns={enableColumnGrouping ? visibleColumns : columns}
          defaultSort={defaultSort}
          includeHeader={headerMode !== 'hidden'}
          includeFooter={includeFooter}
          expandState={expandState}
          onExpandStateChange={setExpandState}
          isLoading={isLoading}
          emptyStateText={emptyStateText}
          getSubRows={getSubRows}
          collapsedGroups={collapsedGroups}
          onToggleColumnGroup={toggleGroup}
          {...tableProps}
        />
      </div>
    </div>
  )
}

PipelinePivotTable.propTypes = {
  pipeline: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  columnConfig: PropTypes.array,
  pivotAccessor: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  pivotValueAccessor: PropTypes.string,
  enableColumnGrouping: PropTypes.bool,
  defaultColumnsExpanded: PropTypes.oneOfType([PropTypes.bool, PropTypes.oneOf(['first'])]),
  getSubRows: PropTypes.func,
  headerMode: PropTypes.oneOf(['hidden', 'default']),
  className: PropTypes.string,
  style: PropTypes.object,
  dataAccessor: PropTypes.string,
  variables: PropTypes.object,
  componentClasses: PropTypes.shape({
    header: PropTypes.string,
    cell: PropTypes.string,
    footer: PropTypes.string
  }),
  componentStyles: PropTypes.shape({
    header: PropTypes.object,
    cell: PropTypes.object,
    highlight: PropTypes.object,
    footer: PropTypes.object
  }),
  emptyStateText: PropTypes.string,
  initialExpandDepth: PropTypes.number,
  allowExport: PropTypes.bool,
  groupingLevels: PropTypes.arrayOf(PropTypes.string),
  groupRowsBy: PropTypes.string
}

PipelinePivotTable.defaultProps = {
  headerMode: 'default',
  dataAccessor: 'data',
  emptyStateText: 'No Data Available',
  allowExport: false,
  getSubRows: (row) => row.data,
  enableColumnGrouping: true,
  defaultColumnsExpanded: false,
  groupRowsBy: 'orionEntityId'
}

export default PipelinePivotTable
